import { useCallback, useEffect, useState } from 'react';
import { AppNoInfo } from '@/layout/AppComponents';
import styled from 'styled-components';
import { Formik } from 'formik';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Scrollbars from 'react-custom-scrollbars';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../../routing/helpers/router';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import Translator from '../../../../../components/helpers/Translator';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import ResponsibleSelect from '../../../../../components/form/Selects/ResponsibleSelect';
import _ from 'lodash';

export default function ResponsibleTab({ elementID }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge,
  );
  const responsibles = useSelector((s) => s.currentPage?.responsibleTab?.[elementID]);
  const [isLoading, setIsLoading] = useState(true);

  // > LOAD RESPONSIBLE TAB
  const loadResponsibles = useCallback(
    async () =>
      await axios
        .post(axiosPath('api_r_user_module_resourceManager_responsible_get'), {
          moduleBridgeHex,
          resourceID: elementID,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                'responsibleTab',
                {
                  [elementID]: data,
                },
                true,
              ),
            );
            setIsLoading(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e)))[(moduleBridgeHex, elementID)],
  );

  useEffect(() => {
    if (responsibles) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
      loadResponsibles();
    }
  }, []);
  // < LOAD RESPONSIBLE TAB

  const handleOnSubmit = useCallback(async (values) => {
    setIsLoading(true);

    axios
      .post(axiosPath('api_r_user_module_resourceManager_responsible_edit'), {
        moduleBridgeHex,
        resourceID: elementID,
        ...values,
      })
      .then(({ data }) => {
        if (isMounted.current) {
          dispatch(
            currentPageAction.change('responsibleTab', { [elementID]: data }, true),
          );
        }
      })
      .catch((e) => dispatch(errorHandler(e)))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) return <AppNoInfo minHeight={300} />;

  return (
    <ResponsibleTabContainer>
      <Scrollbars style={{ height: '400px' }}>
        {userBridge?.permissions?.level === 0 && (
          <Formik
            initialValues={{
              responsibles: _.map(responsibles, 'value'),
            }}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleOnSubmit}
          >
            {({ setFieldValue, handleSubmit, values }) => (
              <>
                <ResponsibleSelect
                  value={values.responsibles}
                  onHandleChange={(opt) => setFieldValue('responsibles', opt)}
                  apiCall="api_r_user_module_resourceManager_responsible_list"
                  hasSearchBox
                  isClearable
                />
                <span
                  className="btn btn-primary btn-xs float-right mb-2"
                  onClick={handleSubmit}
                >
                  <Translator file="general" name="general.update" />
                </span>
                <div className="clearfix" />
              </>
            )}
          </Formik>
        )}
        {Array.isArray(responsibles) && responsibles.length > 0 ? (
          <table className="table">
            <tbody>
              {responsibles.map((r) => (
                <tr key={r.value}>
                  <td>{r.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="alert alert-light">
            <Translator
              file="module-legalCompliance"
              name="no.responsibles.assigned"
            />
          </div>
        )}
      </Scrollbars>
    </ResponsibleTabContainer>
  );
}

const ResponsibleTabContainer = styled.div`
  height: 400px;
  tr {
    & > td {
      padding: 4px;
      font-size: 12px;
      & > div {
        height: 20px;
        overflow-y: hidden;
        text-overflow: ellipsis;
        cursor: pointer;
      }
    }
    & > td:first-child {
      border-top: none;
    }
  }
`;
